/**
 * <tcs-product-rail> — progressive enhancement for scoped TCS product rail
 * variants. Mini rails get the existing arrow-controlled single-slide
 * carousel. Homepage Just For You rails get dot pagination over a native
 * horizontal snap scroller.
 *
 * Native web component, no dependencies — Horizon idiom (see tcs-card.js).
 */
const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

class TcsProductRail extends HTMLElement {
  #track = null;
  #previous = null;
  #next = null;
  #dots = null;
  #dotButtons = [];
  #mode = 'mini';
  #currentIndex = 0;
  #currentPage = 0;
  #pageCount = 1;
  #resizeObserver = null;
  #scrollFrame = 0;
  #targetLeft = null; // in-flight programmatic scroll destination
  #connected = false; // re-init guard: teardown may run before disconnect

  connectedCallback() {
    if (this.#connected) return;

    this.#track = this.querySelector('[data-tcs-rail-track]');
    this.#mode = this.dataset.tcsRailMode || 'mini';
    if (!this.#track) return;

    if (this.#mode === 'dots') {
      this.#dots = this.querySelector('[data-tcs-rail-dots]');
      if (!this.#dots) return;
    } else {
      this.#previous = this.querySelector('[data-tcs-rail-prev]');
      this.#next = this.querySelector('[data-tcs-rail-next]');
      if (!this.#previous || !this.#next) return;
    }

    this.#connected = true;
    this.#currentIndex = 0;
    this.#currentPage = 0;

    if (this.#mode === 'dots') {
      this.#dots.addEventListener('click', this.#onDotClick);
    } else {
      this.#previous.addEventListener('click', this.#onPrevious);
      this.#next.addEventListener('click', this.#onNext);
    }
    // Manual swipes (the no-JS baseline still works) must keep the active
    // index/page in sync.
    this.#track.addEventListener('scroll', this.#onScroll, { passive: true });
    this.#track.addEventListener('touchstart', this.#onUserScrollIntent, { passive: true });
    this.#track.addEventListener('wheel', this.#onUserScrollIntent, { passive: true });
    document.addEventListener('shopify:section:unload', this.#onSectionUnload);

    if ('ResizeObserver' in window) {
      this.#resizeObserver = new ResizeObserver(this.#onResize);
      this.#resizeObserver.observe(this.#track);
    }

    if (this.#mode === 'dots') {
      this.#updateDots();
    } else {
      this.#updateControls();
    }
  }

  disconnectedCallback() {
    this.#teardown();
  }

  #teardown() {
    if (!this.#connected) return;
    this.#connected = false;

    if (this.#mode === 'dots') {
      this.#dots?.removeEventListener('click', this.#onDotClick);
    } else {
      this.#previous?.removeEventListener('click', this.#onPrevious);
      this.#next?.removeEventListener('click', this.#onNext);
    }
    this.#track.removeEventListener('scroll', this.#onScroll);
    this.#track.removeEventListener('touchstart', this.#onUserScrollIntent);
    this.#track.removeEventListener('wheel', this.#onUserScrollIntent);
    document.removeEventListener('shopify:section:unload', this.#onSectionUnload);

    this.#resizeObserver?.disconnect();
    this.#resizeObserver = null;
    this.#dotButtons = [];

    if (this.#scrollFrame) {
      cancelAnimationFrame(this.#scrollFrame);
      this.#scrollFrame = 0;
    }
  }

  #onSectionUnload = (event) => {
    if (event.target.contains(this)) this.#teardown();
  };

  #onPrevious = () => this.#goToSlide(this.#currentIndex - 1);

  #onNext = () => this.#goToSlide(this.#currentIndex + 1);

  #onResize = () => {
    if (this.#mode === 'dots') {
      this.#updateDots();
      this.#goToPage(Math.min(this.#currentPage, this.#pageCount - 1), 'auto');
    } else {
      this.#alignCurrentSlide();
      this.#updateControls();
    }
  };

  // A user gesture cancels any in-flight programmatic scroll — resume syncing
  #onUserScrollIntent = () => {
    this.#targetLeft = null;
  };

  #onScroll = () => {
    if (this.#scrollFrame) return;
    this.#scrollFrame = requestAnimationFrame(() => {
      this.#scrollFrame = 0;
      if (this.#targetLeft !== null) {
        // Programmatic scroll in flight: don't flap states mid-animation
        if (Math.abs(this.#track.scrollLeft - this.#targetLeft) < 2) this.#targetLeft = null;
        return;
      }
      if (this.#mode === 'dots') {
        this.#syncDotState();
        return;
      }
      const slides = this.#slides();
      if (!slides.length || !this.#track.clientWidth) return;
      const index = Math.round(this.#track.scrollLeft / this.#track.clientWidth);
      const clamped = Math.min(Math.max(index, 0), slides.length - 1);
      if (clamped !== this.#currentIndex) {
        this.#currentIndex = clamped;
        this.#setSlideState();
      }
    });
  };

  #slides() {
    return Array.from(this.#track.children);
  }

  #maxScrollLeft() {
    return Math.max(0, this.#track.scrollWidth - this.#track.clientWidth);
  }

  #pageStep() {
    return this.#pageCount > 1 ? this.#maxScrollLeft() / (this.#pageCount - 1) : 0;
  }

  #setSlideState() {
    const slides = this.#slides();
    const active = slides[this.#currentIndex];

    // Focus must never be trapped in a slide about to become inert: hand it
    // to the incoming slide's first focusable, else the next-arrow control.
    const holdsFocus = slides.some(
      (slide, index) => index !== this.#currentIndex && slide.contains(document.activeElement),
    );

    slides.forEach((slide, index) => {
      const inactive = index !== this.#currentIndex;
      slide.setAttribute('aria-hidden', inactive ? 'true' : 'false');
      slide.toggleAttribute('inert', inactive);
    });

    if (holdsFocus) {
      const target = active?.querySelector(
        'a[href], button:not(:disabled), input:not([type="hidden"]), [tabindex]:not([tabindex="-1"])',
      );
      (target || this.#next).focus();
    }
  }

  #updateControls() {
    const hasMultipleItems = this.#slides().length > 1;
    this.#previous.disabled = !hasMultipleItems;
    this.#next.disabled = !hasMultipleItems;
    this.#setSlideState();
  }

  #goToSlide(index) {
    const slides = this.#slides();
    if (!slides.length) return;

    this.#currentIndex = (index + slides.length) % slides.length;
    this.#targetLeft = slides[this.#currentIndex].offsetLeft - this.#track.offsetLeft;
    this.#track.scrollTo({
      left: this.#targetLeft,
      behavior: prefersReducedMotion.matches ? 'auto' : 'smooth',
    });
    this.#updateControls();
  }

  #alignCurrentSlide() {
    const currentSlide = this.#slides()[this.#currentIndex];
    if (!currentSlide) return;
    this.#track.scrollTo({
      left: currentSlide.offsetLeft - this.#track.offsetLeft,
      behavior: 'auto',
    });
  }

  #updateDots() {
    if (!this.#track.clientWidth) {
      this.#pageCount = 1;
      this.#currentPage = 0;
      this.#dots.hidden = true;
      this.#dots.replaceChildren();
      this.#dotButtons = [];
      return;
    }

    const maxScrollLeft = this.#maxScrollLeft();
    this.#pageCount =
      maxScrollLeft > 2 ? Math.ceil(maxScrollLeft / this.#track.clientWidth) + 1 : 1;
    this.#pageCount = Math.max(1, this.#pageCount);

    const needsRender = this.#dotButtons.length !== this.#pageCount;
    if (needsRender) this.#renderDots();

    this.#dots.hidden = this.#pageCount < 2;
    this.#syncDotState();
  }

  // Localized template comes from the section via data attribute
  // ({{ 'tcs.product_rail.dot_label' | t }}); English literal below is a
  // fallback only — no sentence structure is composed in JS.
  #dotLabel(index) {
    const template = this.#dots.dataset.tcsRailDotLabel || 'Go to page [page] of [pages]';
    return template.replace('[page]', index + 1).replace('[pages]', this.#pageCount);
  }

  #renderDots() {
    this.#dots.replaceChildren();
    this.#dotButtons = Array.from({ length: this.#pageCount }, (_, index) => {
      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'tcs-rail__dot';
      button.dataset.tcsRailPage = index;
      button.setAttribute('aria-label', this.#dotLabel(index));
      this.#dots.append(button);
      return button;
    });
  }

  #syncDotState() {
    const step = this.#pageStep();
    const page = step > 0 ? Math.round(this.#track.scrollLeft / step) : 0;
    this.#currentPage = Math.min(Math.max(page, 0), this.#pageCount - 1);

    const currentLabel = this.#dots.dataset.tcsRailDotCurrent || 'Current page';
    this.#dotButtons.forEach((button, index) => {
      const active = index === this.#currentPage;
      button.classList.toggle('is-active', active);
      button.setAttribute(
        'aria-label',
        active ? `${this.#dotLabel(index)}, ${currentLabel}` : this.#dotLabel(index),
      );
      if (active) {
        button.setAttribute('aria-current', 'page');
      } else {
        button.removeAttribute('aria-current');
      }
    });
  }

  #onDotClick = (event) => {
    const button = event.target.closest('[data-tcs-rail-page]');
    if (!button || !this.#dots.contains(button)) return;
    this.#goToPage(Number(button.dataset.tcsRailPage));
  };

  #goToPage(page, behavior = prefersReducedMotion.matches ? 'auto' : 'smooth') {
    if (this.#pageCount < 2) return;
    this.#currentPage = Math.min(Math.max(page, 0), this.#pageCount - 1);
    this.#targetLeft = this.#pageStep() * this.#currentPage;
    this.#track.scrollTo({
      left: this.#targetLeft,
      behavior,
    });
    this.#syncDotState();
  }
}

if (!customElements.get('tcs-product-rail')) {
  customElements.define('tcs-product-rail', TcsProductRail);
}
